<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createAzureBlobConfig } from "./fs";
  import { makeFileSystemAzureBlob, testAzureBlobConnection } from "./fs-azureblob";
  import { captureEvent, telegramNotify } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let config = $state(createAzureBlobConfig());
  let testingConnection = $state(false);
  let errorMessage = $state("");

  let canConnect = $derived(
    config.account.trim() != "" && config.key.trim() != "" && config.container.trim() != "" && !testingConnection,
  );

  async function onConnect() {
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("testing azure blob connection");
    try {
      config.account = config.account.trim().toLowerCase();
      config.key = config.key.trim();
      config.container = config.container.trim();
      await testAzureBlobConnection(config);
      if (config.name.trim() == "") {
        config.name = config.container;
      } else {
        config.name = config.name.trim();
      }
      let fs = makeFileSystemAzureBlob(config);
      let who = config.name;
      telegramNotify(`New Azure Blob connection for ${who}`);
      captureEvent("azureblob-login", { container: config.container });
      removeMsg();
      mountfs(fs);
      open = false;
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect Azure Blob Storage">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-azure-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-azure-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <label class="mt-3" for="input-azure-account">Storage account:</label>
      <input
        type="text"
        id="input-azure-account"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.account}
      />

      <label class="mt-3" for="input-azure-key">Account key:</label>
      <input
        type="password"
        id="input-azure-key"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.key}
      />

      <label class="mt-3" for="input-azure-container">Container:</label>
      <input
        type="text"
        id="input-azure-container"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.container}
      />

      <div class="mt-4 text-sm">
        <a
          href="https://learn.microsoft.com/azure/storage/common/storage-account-keys-manage#view-account-access-keys"
          class="text-blue-600 underline"
          target="_blank"
          rel="noreferrer">Account keys</a
        >
      </div>

      {#if errorMessage}
        <div class="mt-2 mb-2 text-red-500 font-medium break-all w-72">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect Azure Blob</button>
    </div>
  {/snippet}
</DialogBase>
